import riskApi, { riskBaseUrl } from '@Apis/risks.api';
import { ResolvedResponse, SpatialLayer } from '@innovyze/lib_am_common';
import {
	CreateConfig,
	CreateConfigPayload,
	GetRiskMatrixPreviewResponse,
	LogReply,
	RiskGradeRanges,
	RiskMatrixGridObj,
	RiskRunReply,
} from '@Types/risk.types';
import { AxiosResponse } from 'axios';
import { ConfigPublishState, RiskConfig } from '@Types/riskConfig.types';
import { t } from 'i18next';
import {
	ComponentCount,
	ComponentMetadata,
	MultiParamCell,
} from '@Types/component.types';

export const getRiskConfigApi = (
	configId: string,
): Promise<AxiosResponse<RiskConfig>> => {
	const address = `/risk/config?configId=${configId}`;
	return riskApi.get<RiskConfig>(address);
};

export const createRiskConfigApi = (config: CreateConfig) => {
	const address = '/risk/config';
	return riskApi.post<CreateConfigPayload>(address, config);
};

export const saveRiskConfigApi = (config: RiskConfig) => {
	const address = '/risk/config/version';
	return riskApi.post(address, config);
};

export const duplicateRiskConfigApi = (configId: string) => {
	const address = `/risk/config/duplicate?configId=${configId}`;
	return riskApi.post(address);
};

export const startRiskCalculationApi = (configId: string) => {
	const address = `/risk/calc?configId=${configId}`;
	return riskApi.post(address);
};

export const resetRiskCalculationApi = (configId: string) => {
	const address = `/risk/calc/reset?configId=${configId}`;
	return riskApi.put(address);
};

export const resetRiskPreviewCalculationApi = (configId: string) => {
	const address = `/risk/calc/reset?configId=${configId}&isMatrixPreview=true`;
	return riskApi.put(address);
};

export const getRiskDetailsApi = (query: string) => {
	const address = '/risk/score';
	return riskApi.get<ResolvedResponse>(`${address}${query}`);
};

export default class RiskCalcStatusService {
	constructor(private token: string, private configId: string) {}

	public async getProcessStatus(): Promise<RiskRunReply> {
		const response = await fetch(
			`${riskBaseUrl}risk/calc/status?configId=${this.configId}`,
			{
				method: 'GET',
				headers: {
					Authorization: `Bearer ${this.token}`,
					'Content-Type': 'application/json',
					Accept: 'application/json',
				},
			},
		);

		if (response.ok) {
			return await response.json();
		}

		throw new Error(t('Unable to get risk calculation status'));
	}
}

export const getRiskCalcStatusService = (token: string, configId: string) => {
	return new RiskCalcStatusService(token, configId);
};

export const getRiskCalcLogApi = (configId: string) => {
	const address = `/risk/calc/log?configId=${configId}`;
	return riskApi.get<LogReply>(`${address}`);
};

export const getSpatialLayerListApi = () => {
	const address = '/spatial/layerlist';
	return riskApi.get<SpatialLayer[]>(address);
};

export const deleteRiskApi = (id: string) => {
	const address = `/risk/config?configId=${id}`;
	return riskApi.delete(address);
};

export const changeRiskConfigPublishStateApi = (
	newPublishState: ConfigPublishState,
	configId: string,
) => {
	const address = `/risk/config/publish?configId=${configId}`;
	return riskApi.put(address, { newPublishState });
};

const getRiskCountApi = (
	assetType: string,
	fieldType: string,
	metadata: ComponentMetadata[],
	metadataType: string,
	additionalParams?: Record<string, string | number>,
) => {
	const params = new URLSearchParams({
		assetType,
		fieldType,
		...additionalParams,
	});
	const address = `/risk/count?${params.toString()}`;

	return riskApi.post(address, {
		metadata,
		metadataType,
	});
};

export const getRiskCountInspectionApi = (
	metadata: ComponentMetadata[],
	metadataType: string,
	assetType: string,
	fieldName: string,
	inspectionType: string,
) => {
	return getRiskCountApi(assetType, 'inspection', metadata, metadataType, {
		fieldName,
		inspectionType: inspectionType.toUpperCase(),
	});
};

export const getRiskCountAtributeApi = (
	metadata: ComponentMetadata[],
	metadataType: string,
	assetType: string,
	fieldName: string,
) => {
	return getRiskCountApi(assetType, 'attribute', metadata, metadataType, {
		fieldName,
	});
};

export const getRiskCountObservationApi = (
	metadata: ComponentMetadata[],
	metadataType: string,
	assetType: string,
	fieldName: string,
) => {
	fieldName = fieldName === 'Code' ? 'PACP_CODE' : fieldName;
	return getRiskCountApi(assetType, 'observation', metadata, metadataType, {
		fieldName,
	});
};

export const getRiskCountProximityApi = (
	metadata: ComponentMetadata[],
	metadataType: string,
	assetType: string,
	layerName: string,
) => {
	return getRiskCountApi(assetType, 'proximity', metadata, metadataType, {
		layerName,
	});
};

export const getRiskCountProximityAtributeApi = (
	metadata: ComponentMetadata[],
	metadataType: string,
	assetType: string,
	layerName: string,
	fieldName: string,
	valueType: string,
	distance: number,
) => {
	return getRiskCountApi(
		assetType,
		'proximityAttribute',
		metadata,
		metadataType,
		{
			layerName,
			fieldName,
			maxDistance: distance,
		},
	);
};

export const getRiskCountCustomTableApi = (
	metadata: ComponentMetadata[],
	metadataType: string,
	assetType: string,
	fieldName: string,
	tableName: string,
	valueType: string,
) => {
	return getRiskCountApi(assetType, 'customTable', metadata, metadataType, {
		fieldName,
		tableName,
		valueType,
	});
};

export const getRiskCountTaskApi = (
	metadata: ComponentMetadata[],
	metadataType: string,
	assetType: string,
	fieldName: string,
	tableName: string,
	valueType: string,
	uploadId: string,
) => {
	const address = `/risk/count?assetType=${assetType}&fieldType=task&fieldName=${fieldName}&tableName=${tableName}&uploadId=${uploadId}&valueType=${valueType}`;
	return riskApi.post(address, {
		metadata: metadata,
		metadataType: metadataType,
	});
};

export const getSimulationDataApi = () => {
	return riskApi.get('/risk/simulations');
};

export const getTaskDataApi = (assetType: string) => {
	return riskApi.get(`/risk/tasks?assetType=${assetType}`);
};

export const getRiskCountScoreMatrixApi = (
	components: ComponentCount[],
	scoreMatrix: MultiParamCell[],
	assetType: string,
	inspectionType?: string,
) => {
	let address = `/risk/count?assetType=${assetType}&fieldType=multiParam`;
	if (inspectionType)
		address += `&inspectionType=${inspectionType.toUpperCase()}`;

	return riskApi.post(address, {
		paramComponents: components,
		scoreMatrix: scoreMatrix,
	});
};

export const initRiskPreviewApi = (
	cofWeighting: number,
	lofWeighting: number,
	riskMethod: string,
	grades: RiskGradeRanges,
	grid: RiskMatrixGridObj[],
	preview: {
		grades: RiskGradeRanges;
		grid: RiskMatrixGridObj[];
	},
	configId: string,
) => {
	const address = `/risk/calc?configId=${configId}&isMatrixPreview=true`;

	return riskApi.post(address, {
		cofWeighting,
		lofWeighting,
		riskMethod,
		grades,
		grid,
		preview,
	});
};

export const getRiskPreviewApi = (configId: string) => {
	const address = `risk/preview/status?configId=${configId}`;

	return riskApi.get<GetRiskMatrixPreviewResponse>(address);
};
